import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { ErrorBoundary, ErrorRetry, SuspenseLoader, useAppUser } from '@bechtel/client-common';
import { GET_PROJECT_ITR_CONFIG, SAVE_ITR_CONFIG } from '@bechtel/client-gql';
import {
  Box,
  ButtonAppearance,
  FormControl,
  Select,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  ItrMilestoneAction,
  ItrType,
  ProjectConfig,
  ProjectItrConfig,
  SaveItrConfigInput,
  SaveItrConfigPayload,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { Formik } from 'formik';
import { every, filter, flatMap, get, map, some, toUpper } from 'lodash';
import { Fragment, Suspense, useEffect, useState } from 'react';
import ItrConfigError from '~/quantity-reporting/inspection-test-record/error';
import {
  FooterButtonStyled,
  FooterStyled,
  ProjectAdminContainerStyled,
  ProjectAdminSubContainerStyled,
} from '../../styled';
import { SaveItrConfigErrorExt } from './error/types';
import { milestoneActionList, milestoneNoneType, noItrMiestoneSelection } from './form-data';
import { ItrConfigItem, MilestoneItem, MilestoneSelectedItem } from './item';
import { FormikState, ItrConfig, MilestoneTypeId } from './types';

const ItrConfigUpdateToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Inspection Test Record Certification data saved successfully.</Trans>
      </Text>
    </Box>
  );
};

let formikInitialValues: FormikState = {
  itrConfigList: [],
  itrMilestoneAction: ItrMilestoneAction.CLAIM,
};

export const InspectionTestRecord = () => {
  const { user } = useAppUser();
  const [initialValues, setInitialValues] = useState<FormikState>(formikInitialValues);
  const dispatchToast = useToastsDispatch();
  const [saveItrConfigError, setSaveItrConfigError] = useState<SaveItrConfigErrorExt | null>();
  const { loading, error, data, refetch, networkStatus } = useQuery<{
    projectConfig: ProjectConfig;
  }>(GET_PROJECT_ITR_CONFIG, {
    variables: { id: user?.project?.id },
    notifyOnNetworkStatusChange: true,
  });
  const projectConfigData: ProjectConfig = get(data, 'projectConfigById', undefined);

  const projectItrConfigList: ProjectItrConfig[] = get(projectConfigData, 'projectItrConfig', []);
  useEffect(() => {
    if (!projectConfigData || !projectConfigData.projectItrConfig) return;
    const projectCommodityTypeItrs = flatMap(projectConfigData.projectItrConfig, (item: ProjectItrConfig) => {
      return item.projectCommodityTypeItrs;
    });
    const ItrConfigData: ItrConfig[] =
      projectCommodityTypeItrs.length > 0
        ? projectCommodityTypeItrs.map(({ id, projectMilestoneType }) => ({
            projectCommodityTypeItrId: id,
            projectMilestoneType: projectMilestoneType ? projectMilestoneType : milestoneNoneType,
          }))
        : [];
    formikInitialValues = {
      itrConfigList: [...ItrConfigData],
      itrMilestoneAction: projectItrConfigList[0]?.itrMilestoneAction
        ? projectItrConfigList[0]?.itrMilestoneAction
        : ItrMilestoneAction.CLAIM,
    };
    setInitialValues(formikInitialValues);
  }, [projectConfigData]);

  const [saveItrConfig, { loading: mutationLoading }] = useMutation<
    { saveItrConfigPayload: SaveItrConfigPayload },
    { input: SaveItrConfigInput[] }
  >(SAVE_ITR_CONFIG, {
    onCompleted: (data) => {
      const itrSaveError = get(data, 'saveItrConfig.error');
      if (itrSaveError) {
        setSaveItrConfigError(itrSaveError);
      } else {
        setSaveItrConfigError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: ItrConfigUpdateToast,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
      }
    },
    onError: (error) => {
      setSaveItrConfigError(error);
    },
  });

  const closeModal = () => {
    setSaveItrConfigError(null);
  };

  return (
    <Fragment>
      <ProjectAdminContainerStyled style={{ overflow: 'hidden' }}>
        {(loading || networkStatus === NetworkStatus.refetch) && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="itr-loading"
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}
        {!loading && error && (
          <div data-testid="itr-error-retry">
            <ErrorRetry refetch={refetch}>
              <Trans>
                There was a problem loading Inspection Test Record data. Please click the button below to try again.
              </Trans>
            </ErrorRetry>
          </div>
        )}

        {!loading && !error && projectItrConfigList.length > 0 && (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={async (values) => {
              const modifiedItems = filter(values.itrConfigList, (item) => {
                return some(initialValues.itrConfigList, (defaultItem) => {
                  return (
                    defaultItem.projectCommodityTypeItrId === item.projectCommodityTypeItrId &&
                    defaultItem.projectMilestoneType !== item.projectMilestoneType
                  );
                });
              });

              const itrMilestoneConfigInput = modifiedItems.map(
                ({ projectCommodityTypeItrId, projectMilestoneType }) => ({
                  projectCommodityTypeItrId,
                  projectMilestoneTypeId:
                    projectMilestoneType.id === milestoneNoneType.id ? null : projectMilestoneType.id,
                })
              );
              let input: SaveItrConfigInput[] = [];
              if (
                every(
                  values?.itrConfigList,
                  (itrConfig) => toUpper(itrConfig.projectMilestoneType?.id) === MilestoneTypeId.NONE
                )
              ) {
                input = [
                  {
                    itrMilestoneConfigInput: itrMilestoneConfigInput,
                    itrType: ItrType.PIPE_CLEANLINESS,
                  },
                ];
              } else {
                input = [
                  {
                    itrMilestoneAction: values?.itrMilestoneAction,
                    itrMilestoneConfigInput: itrMilestoneConfigInput,
                    itrType: ItrType.PIPE_CLEANLINESS,
                  },
                ];
              }

              await saveItrConfig({
                variables: { input },
              });
            }}
          >
            {(formikProps) => {
              const { dirty, isValid, handleReset, handleSubmit, setFieldValue, values } = formikProps;
              return (
                <Fragment>
                  <ProjectAdminSubContainerStyled data-testid="itr-container">
                    <Box
                      p={{ sm: '550' }}
                      flexGrow={{ sm: 1 }}
                      flexDirection={{ sm: 'column' }}
                      css={`
                        overflow-y: auto;
                      `}
                    >
                      <Box mb={{ sm: '400' }}>
                        <Text size="500" family="graphik" weight="600" color="grey.1000">
                          <Trans>Inspection Test Record Certification</Trans>
                        </Text>
                      </Box>
                      {map(projectItrConfigList, (projectItrConfigItem) => {
                        return (
                          <ItrConfigItem
                            formikProps={formikProps}
                            key={projectItrConfigItem.id}
                            projectItrConfig={projectItrConfigItem}
                          />
                        );
                      })}

                      <Box my={{ sm: '450' }}>
                        <FormControl
                          label={t`Milestone Action`}
                          css={`
                            flex-grow: 1;
                          `}
                        >
                          <Select
                            placeholder={t`Milestone Action`}
                            size={TextFieldSize.LARGE}
                            appearance={TextFieldAppearance.NORMAL}
                            items={milestoneActionList}
                            itemComponent={MilestoneItem}
                            selectedComponent={MilestoneSelectedItem}
                            selectedItem={
                              every(
                                values?.itrConfigList,
                                (itrConfig) =>
                                  toUpper(itrConfig?.projectMilestoneType?.id.toUpperCase()) === MilestoneTypeId.NONE
                              )
                                ? noItrMiestoneSelection
                                : filter(
                                    milestoneActionList,
                                    (milestoneAction) => milestoneAction?.value == values?.itrMilestoneAction
                                  )[0]
                            }
                            onSelectedItemChange={async (changes: any) => {
                              setFieldValue('itrMilestoneAction', changes?.selectedItem?.value);
                            }}
                            disabled={every(
                              values?.itrConfigList,
                              (itrConfig) => toUpper(itrConfig.projectMilestoneType?.id) === MilestoneTypeId.NONE
                            )}
                          />
                        </FormControl>
                      </Box>
                    </Box>
                  </ProjectAdminSubContainerStyled>
                  {!loading && !error && (
                    <FooterStyled>
                      <Box>
                        <Box mr={{ sm: '400' }}>
                          <FooterButtonStyled
                            data-testid="itr-cancel"
                            disabled={mutationLoading || !dirty}
                            onClick={() => {
                              handleReset();
                            }}
                            appearance={ButtonAppearance.SECONDARY}
                          >
                            <Trans>Discard Changes</Trans>
                          </FooterButtonStyled>
                        </Box>
                        <FooterButtonStyled
                          data-testid="itr-save"
                          onClick={() => {
                            handleSubmit();
                          }}
                          disabled={mutationLoading || !dirty || !isValid}
                          appearance={ButtonAppearance.SECONDARY}
                        >
                          {mutationLoading && <Spinner />}
                          {!mutationLoading && t`Save Changes`}
                        </FooterButtonStyled>
                      </Box>
                    </FooterStyled>
                  )}
                </Fragment>
              );
            }}
          </Formik>
        )}
      </ProjectAdminContainerStyled>
      {saveItrConfigError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <ItrConfigError error={saveItrConfigError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
